"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBreakpoint } from "react-use";
import type { DropInteractionParams } from "./drop.types";
import { DropLocation } from "./drop.types";
import { getRankHoverRingClass } from "./dropRankStyles";
import type { BoostAnimationState } from "./DropBoostAnimation";
import DropBoostAnimation from "./DropBoostAnimation";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
const GROUPING_TIME_DIFFERENCE_MS = 60_000;
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GROUPING_TIME_DIFFERENCE_MS;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(1)}`,
  2: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(2)}`,
  3: `tw-ring-1 tw-ring-inset tw-ring-iron-700/35 ${getRankHoverRingClass(3)}`,
  default: "tw-ring-1 tw-ring-inset tw-ring-iron-700/35 desktop-hover:hover:tw-ring-iron-600/45"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-ring-1 tw-ring-inset tw-ring-[#3CCB7F]/55";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = isWaveView ? "" : "tw-ring-1 tw-ring-inset tw-ring-iron-800";
    const bgClass = isWaveView ? "" : "tw-bg-iron-950/80";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES];
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly wrapContentOnly?: ((content: React.ReactNode) => React.ReactNode) | undefined;
}
const WaveDrop = t0 => {
  const $ = _c(146);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    wrapContentOnly
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activeLinkCardActionIds, setActiveLinkCardActionIds] = useState(t1);
  const [boostAnimation, setBoostAnimation] = useState(null);
  const dropRef = useRef(null);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  let t2;
  if ($[1] !== drop || $[2] !== isDrop || $[3] !== previousDrop) {
    t2 = !isDrop && shouldGroupWithDrop(drop, previousDrop);
    $[1] = drop;
    $[2] = isDrop;
    $[3] = previousDrop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shouldGroupWithPreviousDrop = t2;
  let t3;
  if ($[5] !== drop || $[6] !== isDrop || $[7] !== nextDrop) {
    t3 = !isDrop && shouldGroupWithDrop(drop, nextDrop);
    $[5] = drop;
    $[6] = isDrop;
    $[7] = nextDrop;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const shouldGroupWithNextDrop = t3;
  const isMobile = useIsMobileDevice();
  const hasTouch = useHasTouchInput() || isMobile;
  const breakpoint = useBreakpoint();
  const isMdUp = breakpoint === "MD";
  const allowLongPress = hasTouch && !isMdUp;
  const compact = useCompactMode();
  const hasActiveLinkCardActions = activeLinkCardActionIds.length > 0;
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = !shouldGroupWithPreviousDrop || isProfileView;
  let t4;
  if ($[9] !== isProfileView || $[10] !== shouldGroupWithNextDrop || $[11] !== shouldGroupWithPreviousDrop) {
    t4 = () => {
      if (isProfileView) {
        return "tw-py-4";
      }
      if (shouldGroupWithPreviousDrop) {
        return "tw-pt-1";
      }
      if (shouldGroupWithNextDrop) {
        return "tw-pt-4 tw-pb-1";
      }
      return "tw-py-4";
    };
    $[9] = isProfileView;
    $[10] = shouldGroupWithNextDrop;
    $[11] = shouldGroupWithPreviousDrop;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const getGroupingClass = t4;
  const groupingClass = getGroupingClass();
  const replyTo = drop.reply_to;
  const isGroupedReplyWithPrevious = shouldGroupWithPreviousDrop && replyTo?.drop_id === previousDrop?.reply_to?.drop_id;
  const shouldShowReplyHeader = !!replyTo && replyTo.drop_id !== dropViewDropId && !isGroupedReplyWithPrevious;
  let t5;
  if ($[13] !== allowLongPress || $[14] !== dispatch || $[15] !== editingDropId) {
    t5 = () => {
      if (!allowLongPress) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[13] = allowLongPress;
    $[14] = dispatch;
    $[15] = editingDropId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleLongPress = t5;
  let t6;
  if ($[17] !== allowLongPress || $[18] !== handleLongPress || $[19] !== isEditing) {
    t6 = e => {
      if (!allowLongPress) {
        return;
      }
      if (isEditing) {
        return;
      }
      const touch = e.touches[0];
      touchStartPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      longPressTimeoutRef.current = setTimeout(handleLongPress, 500);
    };
    $[17] = allowLongPress;
    $[18] = handleLongPress;
    $[19] = isEditing;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleTouchStart = t6;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      touchStartPosition.current = null;
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleTouchEnd = t7;
  let t8;
  if ($[22] !== allowLongPress) {
    t8 = e_0 => {
      if (!allowLongPress) {
        return;
      }
      if (!touchStartPosition.current) {
        return;
      }
      const touch_0 = e_0.touches[0];
      const deltaX = Math.abs(touch_0.clientX - touchStartPosition.current.x);
      const deltaY = Math.abs(touch_0.clientY - touchStartPosition.current.y);
      if ((deltaX > 10 || deltaY > 10) && longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    $[22] = allowLongPress;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleTouchMove = t8;
  let t9;
  if ($[24] !== activePartIndex || $[25] !== dispatch || $[26] !== drop || $[27] !== editingDropId || $[28] !== onReply) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[24] = activePartIndex;
    $[25] = dispatch;
    $[26] = drop;
    $[27] = editingDropId;
    $[28] = onReply;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleOnReply = t9;
  let t10;
  if ($[30] !== dispatch || $[31] !== editingDropId) {
    t10 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[30] = dispatch;
    $[31] = editingDropId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_1 => {
      e_1.stopPropagation();
      setLongPressTriggered(false);
      setIsSlideUp(true);
    };
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleOpenTouchActions = t11;
  let t12;
  if ($[34] !== dispatch || $[35] !== drop.id) {
    t12 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[34] = dispatch;
    $[35] = drop.id;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleOnEdit = t12;
  let t13;
  if ($[37] !== activePartIndex || $[38] !== dispatch || $[39] !== drop || $[40] !== dropUpdateMutation) {
    t13 = (newContent, mentions, mentionedWaves) => {
      const cleanedMentions = (mentions ?? drop.mentioned_users).map(_temp);
      const cleanedWaves = (mentionedWaves ?? drop.mentioned_waves).map(_temp2);
      const updateRequest = {
        parts: drop.parts.map((part, index) => ({
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop ?? null,
          media: part.media
        })),
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        mentioned_waves: cleanedWaves,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[37] = activePartIndex;
    $[38] = dispatch;
    $[39] = drop;
    $[40] = dropUpdateMutation;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const handleEditSave = t13;
  let t14;
  if ($[42] !== dispatch) {
    t14 = () => {
      dispatch(setEditingDropId(null));
    };
    $[42] = dispatch;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const handleEditCancel = t14;
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setBoostAnimation(null);
    };
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const handleBoostAnimationComplete = t15;
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = (actionId, active) => {
      setActiveLinkCardActionIds(current => {
        const hasActionId = current.includes(actionId);
        if (active) {
          return hasActionId ? current : [...current, actionId];
        }
        return hasActionId ? current.filter(item => item !== actionId) : current;
      });
    };
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const handleLinkCardActionsActiveChange = t16;
  let t17;
  if ($[46] !== drop.context_profile_context?.boosted || $[47] !== drop.id) {
    t17 = () => {
      if (!dropRef.current) {
        return;
      }
      const rect = dropRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const isBoosted = drop.context_profile_context?.boosted ?? false;
      setBoostAnimation({
        id: `${drop.id}-${Date.now()}`,
        x,
        y,
        type: isBoosted ? "unboost" : "boost"
      });
    };
    $[46] = drop.context_profile_context?.boosted;
    $[47] = drop.id;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  drop.context_profile_context?.boosted;
  const handleMobileBoostAnimation = t17;
  let t18;
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    t19 = [];
    $[49] = t18;
    $[50] = t19;
  } else {
    t18 = $[49];
    t19 = $[50];
  }
  useEffect(t18, t19);
  const effectiveIsSlideUp = isSlideUp && !isEditing;
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  let t20;
  if ($[51] !== drop.wave || $[52] !== onReplyClick || $[53] !== replyTo || $[54] !== shouldShowReplyHeader) {
    t20 = shouldShowReplyHeader && <WaveDropReply onReplyClick={onReplyClick} dropId={replyTo.drop_id} dropPartId={replyTo.drop_part_id} maybeDrop={replyTo.drop ? {
      ...replyTo.drop,
      wave: drop.wave
    } : null} />;
    $[51] = drop.wave;
    $[52] = onReplyClick;
    $[53] = replyTo;
    $[54] = shouldShowReplyHeader;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== drop || $[57] !== showAuthorInfo) {
    t21 = showAuthorInfo && <WaveDropAuthorPfp drop={drop} />;
    $[56] = drop;
    $[57] = showAuthorInfo;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const t22 = showAuthorInfo ? "calc(100% - 3.5rem)" : "100%";
  let t23;
  if ($[59] !== t22) {
    t23 = {
      maxWidth: t22
    };
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== activePartIndex || $[62] !== drop || $[63] !== handleOpenTouchActions || $[64] !== hasTouch || $[65] !== isEditing || $[66] !== isStorm || $[67] !== showAuthorInfo || $[68] !== showReplyAndQuote || $[69] !== showWaveInfo) {
    t24 = showAuthorInfo && <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} showActionsButton={hasTouch && showReplyAndQuote && !isEditing} onOpenActions={handleOpenTouchActions} />;
    $[61] = activePartIndex;
    $[62] = drop;
    $[63] = handleOpenTouchActions;
    $[64] = hasTouch;
    $[65] = isEditing;
    $[66] = isStorm;
    $[67] = showAuthorInfo;
    $[68] = showReplyAndQuote;
    $[69] = showWaveInfo;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  const t25 = `tw-w-full ${showAuthorInfo ? "tw-mt-2" : ""}${shouldGroupWithPreviousDrop && !isProfileView ? "tw-pl-[3.25rem]" : ""}`;
  let t26;
  if ($[71] !== activePartIndex || $[72] !== allowLongPress || $[73] !== drop || $[74] !== dropUpdateMutation.isPending || $[75] !== handleEditCancel || $[76] !== handleEditSave || $[77] !== handleLinkCardActionsActiveChange || $[78] !== handleLongPress || $[79] !== isEditing || $[80] !== onDropContentClick || $[81] !== onQuoteClick) {
    t26 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isEditing={isEditing} isSaving={dropUpdateMutation.isPending} onSave={handleEditSave} onCancel={handleEditCancel} hasTouch={allowLongPress} onLinkCardActionsActiveChange={handleLinkCardActionsActiveChange} />;
    $[71] = activePartIndex;
    $[72] = allowLongPress;
    $[73] = drop;
    $[74] = dropUpdateMutation.isPending;
    $[75] = handleEditCancel;
    $[76] = handleEditSave;
    $[77] = handleLinkCardActionsActiveChange;
    $[78] = handleLongPress;
    $[79] = isEditing;
    $[80] = onDropContentClick;
    $[81] = onQuoteClick;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== t25 || $[84] !== t26) {
    t27 = <div className={t25}>{t26}</div>;
    $[83] = t25;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== t23 || $[87] !== t24 || $[88] !== t27) {
    t28 = <div className="tw-flex tw-w-full tw-flex-col" style={t23}>{t24}{t27}</div>;
    $[86] = t23;
    $[87] = t24;
    $[88] = t27;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== t21 || $[91] !== t28) {
    t29 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t21}{t28}</div>;
    $[90] = t21;
    $[91] = t28;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== activePartIndex || $[94] !== drop || $[95] !== handleOnEdit || $[96] !== handleOnReply || $[97] !== hasActiveLinkCardActions || $[98] !== isEditing || $[99] !== isMobile || $[100] !== showReplyAndQuote) {
    t30 = !isMobile && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onEdit={handleOnEdit} suppressed={hasActiveLinkCardActions} />;
    $[93] = activePartIndex;
    $[94] = drop;
    $[95] = handleOnEdit;
    $[96] = handleOnReply;
    $[97] = hasActiveLinkCardActions;
    $[98] = isEditing;
    $[99] = isMobile;
    $[100] = showReplyAndQuote;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== t20 || $[103] !== t29 || $[104] !== t30) {
    t31 = <>{t20}{t29}{t30}</>;
    $[102] = t20;
    $[103] = t29;
    $[104] = t30;
    $[105] = t31;
  } else {
    t31 = $[105];
  }
  const contentBlock = t31;
  const t32 = `tw-mx-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${compact ? "tw-ml-11 tw-w-[calc(100%-2.5rem)]" : "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]"}`;
  let t33;
  if ($[106] !== drop.metadata) {
    t33 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[106] = drop.metadata;
    $[107] = t33;
  } else {
    t33 = $[107];
  }
  let t34;
  if ($[108] !== drop) {
    t34 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[108] = drop;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  let t35;
  if ($[110] !== drop) {
    t35 = <WaveDropReactions drop={drop} />;
    $[110] = drop;
    $[111] = t35;
  } else {
    t35 = $[111];
  }
  let t36;
  if ($[112] !== t32 || $[113] !== t33 || $[114] !== t34 || $[115] !== t35) {
    t36 = <div className={t32}>{t33}{t34}{t35}</div>;
    $[112] = t32;
    $[113] = t33;
    $[114] = t34;
    $[115] = t35;
    $[116] = t36;
  } else {
    t36 = $[116];
  }
  const reactionsRow = t36;
  const t37 = `${isDrop && location === DropLocation.WAVE ? "tw-px-4 tw-py-0.5" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  let t38;
  if ($[117] !== contentBlock || $[118] !== wrapContentOnly) {
    t38 = wrapContentOnly ? wrapContentOnly(contentBlock) : contentBlock;
    $[117] = contentBlock;
    $[118] = wrapContentOnly;
    $[119] = t38;
  } else {
    t38 = $[119];
  }
  let t39;
  if ($[120] !== drop || $[121] !== effectiveIsSlideUp || $[122] !== handleMobileBoostAnimation || $[123] !== handleOnAddReaction || $[124] !== handleOnEdit || $[125] !== handleOnReply || $[126] !== longPressTriggered || $[127] !== showReplyAndQuote) {
    t39 = <WaveDropMobileMenu drop={drop} isOpen={effectiveIsSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} onBoostAnimation={handleMobileBoostAnimation} />;
    $[120] = drop;
    $[121] = effectiveIsSlideUp;
    $[122] = handleMobileBoostAnimation;
    $[123] = handleOnAddReaction;
    $[124] = handleOnEdit;
    $[125] = handleOnReply;
    $[126] = longPressTriggered;
    $[127] = showReplyAndQuote;
    $[128] = t39;
  } else {
    t39 = $[128];
  }
  let t40;
  if ($[129] !== boostAnimation || $[130] !== handleBoostAnimationComplete) {
    t40 = <DropBoostAnimation animation={boostAnimation} onComplete={handleBoostAnimationComplete} />;
    $[129] = boostAnimation;
    $[130] = handleBoostAnimationComplete;
    $[131] = t40;
  } else {
    t40 = $[131];
  }
  let t41;
  if ($[132] !== drop.serial_no || $[133] !== drop.stableHash || $[134] !== dropClasses || $[135] !== handleTouchEnd || $[136] !== handleTouchMove || $[137] !== handleTouchStart || $[138] !== reactionsRow || $[139] !== t38 || $[140] !== t39 || $[141] !== t40) {
    t41 = <div ref={dropRef} className={dropClasses} data-wave-drop-id={drop.stableHash} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t38}{reactionsRow}{t39}{t40}</div>;
    $[132] = drop.serial_no;
    $[133] = drop.stableHash;
    $[134] = dropClasses;
    $[135] = handleTouchEnd;
    $[136] = handleTouchMove;
    $[137] = handleTouchStart;
    $[138] = reactionsRow;
    $[139] = t38;
    $[140] = t39;
    $[141] = t40;
    $[142] = t41;
  } else {
    t41 = $[142];
  }
  let t42;
  if ($[143] !== t37 || $[144] !== t41) {
    t42 = <div className={t37}>{t41}</div>;
    $[143] = t37;
    $[144] = t41;
    $[145] = t42;
  } else {
    t42 = $[145];
  }
  return t42;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}
function _temp2(wave) {
  return {
    wave_id: wave.wave_id,
    wave_name_in_content: wave.wave_name_in_content
  };
}